import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getOrderClaimForm, setOrderClaimForm } from 'domains/order/Order.store';
import { ClaimLabourInfo } from 'domains/order/Order.types';
import { getTextWithMandatoryFlag, isHidden } from 'pages/MyOrdersPage/OrderClaims/claimForm.utils';
import { makeKey, RULES_DATA } from 'pages/MyOrdersPage/OrderClaims/claimFormRulesData';
import { FlexRow } from 'pages/MyOrdersPage/OrderClaims/OrderClaims.styled';
import { DatePicker, Flex, InputNumber, InputTime, Switch, Text } from 'UI';
import { getMomentDate, getStringDateFromMoment } from 'utils/date/dateConverter';

const LabourSection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const claimForm = useSelector(getOrderClaimForm);
  const rulesData =
    RULES_DATA[makeKey({ formType: claimForm.formType, reason: claimForm.reason, option: claimForm.option })];

  const labour: ClaimLabourInfo = claimForm.labour ?? { isFitted: true };

  const updateLabour = (patch: Partial<ClaimLabourInfo>) =>
    dispatch(setOrderClaimForm({ ...claimForm, labour: { ...labour, ...patch } }));

  useEffect(() => {
    if (!isHidden(rulesData?.labour) && !labour.isFitted) {
      updateLabour({ isFitted: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesData?.labour]);

  if (isHidden(rulesData?.labour)) {
    return null;
  }

  return (
    <>
      <Flex gap={15} align={'center'}>
        <Switch
          checked={labour.isFitted}
          onChange={(isFitted: boolean) => updateLabour({ isFitted })}
          data-cy={'claim_labour_is_fitted_switch'}
        />
        <Text type="base">{t('order.claim.labour.fitted', 'Has the spare part been fitted?')}</Text>
      </Flex>
      {labour.isFitted && (
        <FlexRow gap={30} align={'flex-end'}>
          <Flex direction={'column'} maxWidth={200}>
            <Text type="base">
              {getTextWithMandatoryFlag(
                t('order.claim.labour.labour_time', 'Labour time for fitting or dismantling'),
                true,
              )}
            </Text>
            <InputTime
              bordered
              initialValue={labour.labourTime}
              onChangeDebounced={(labourTime: string) => updateLabour({ labourTime })}
            />
          </Flex>
          <Flex direction={'column'} maxWidth={200}>
            <Text type="base">
              {getTextWithMandatoryFlag(t('order.claim.labour.fitting_date', 'Date of fitting'), true)}
            </Text>
            <DatePicker
              bordered
              value={getMomentDate(labour.fittingDate) ?? null}
              onChange={(_value: moment.Moment | null, _dateString: string) =>
                updateLabour({ fittingDate: getStringDateFromMoment(_value) })
              }
            />
          </Flex>
          <Flex direction={'column'} maxWidth={200}>
            <Text type="base">
              {getTextWithMandatoryFlag(
                t('order.claim.labour.fitting_mileage', 'Mileage at the time of fitting'),
                true,
              )}
            </Text>
            <InputNumber
              bordered
              initialValue={labour.fittingMileage}
              onChangeDebounced={(fittingMileage: number | undefined) => updateLabour({ fittingMileage })}
              maxLength={10}
            />
          </Flex>
          <Flex direction={'column'} maxWidth={200}>
            <Text type="base">
              {getTextWithMandatoryFlag(t('order.claim.labour.dismantling_date', 'Date of dismantling'), true)}
            </Text>
            <DatePicker
              bordered
              value={getMomentDate(labour.dismantlingDate) ?? null}
              onChange={(_value: moment.Moment | null, _dateString: string) =>
                updateLabour({ dismantlingDate: getStringDateFromMoment(_value) })
              }
            />
          </Flex>
          <Flex direction={'column'} maxWidth={200}>
            <Text type="base">
              {getTextWithMandatoryFlag(
                t('order.claim.labour.dismantling_mileage', 'Mileage at the time of dismantling'),
                true,
              )}
            </Text>
            <InputNumber
              bordered
              initialValue={labour.dismantlingMileage}
              onChangeDebounced={(dismantlingMileage: number | undefined) => updateLabour({ dismantlingMileage })}
              maxLength={10}
            />
          </Flex>
        </FlexRow>
      )}
    </>
  );
};

export default LabourSection;
